import { CustomQuestionsSettings } from 'client/components/core/Sidebar/components/Settings/entities/BookingCalendarSettings/custom-questions-settings'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { ShadowStyle } from 'client/components/core/Sidebar/components/Settings/styles'
import Border from 'client/components/core/Sidebar/components/Settings/styles/Border'
import MarginStyle from 'client/components/core/Sidebar/components/Settings/styles/MarginStyle'
import PaddingStyle from 'client/components/core/Sidebar/components/Settings/styles/SplitPaddingStyle'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useMyEvents } from 'client/hooks/use-my-events'
import useManagement, {
  selectors as managementSelectors,
} from 'client/hooks/useManagement'
import { MyEventInterface } from 'common/components/entities/Calendar/calendar-interface'
import { BookingCalendarSteps } from 'common/components/entities/Calendar/constants'
import { BookingCalendarInterface } from 'common/types/entities/BookingCalendarInterface'
import { MarginType } from 'common/types/styleTypes'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { SelectNew } from 'src/editor/shared/ui-lib/select-new/select-new'
import { BookingStepsSwitcher } from './booking-steps-switcher'

export interface BookingCalendarSettingsProps {
  entity: BookingCalendarInterface
}

const BookingCalendarSettings = ({ entity }: BookingCalendarSettingsProps) => {
  const stepType = useManagement(managementSelectors.getBookingCalendarStepType)
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<BookingCalendarInterface>()
  const { isMobile } = useDeviceModeContext()
  const { t } = useTranslation()
  const { events, isFetching } = useMyEvents({
    onSuccess: events => {
      if (events.length) {
        if (entity?.eventId) {
          const event = events.find(({ id }) => id === entity.eventId)
          if (event) {
            setSelectedEvent(event)
            return
          }
        }

        if (events.length === 1) {
          setSelectedEvent(events[0])
          onEventSelect(events[0])
          return
        }

        onEventRemove('validation.page.calendar.event_not_selected')
        return
      }
      onEventRemove('entity_settings.calendar.events_empty_list_error')
    },
  })
  const [error, setError] = useState('')
  const isFormStep = stepType === BookingCalendarSteps.FormStep

  const [selectedEvent, setSelectedEvent] = useState<MyEventInterface | null>(
    null,
  )

  const onEventSelect = async (event: MyEventInterface) => {
    setSelectedEvent(event)
    const updatedEntity = {
      ...entity,
      eventId: event.id,
    }
    updateEntity(updatedEntity)
    if (error) setError('')
  }

  const onEventRemove = (error = '') => {
    const updatedEntity = {
      ...entity,
      eventId: null,
    }
    updateEntity(updatedEntity)
    if (error) setError(t(error))
  }

  return (
    <React.Fragment>
      <BookingStepsSwitcher entity={entity} />
      <GroupTitle>entity_settings.booking_calendar.event</GroupTitle>
      <SelectNew
        isMulti={false}
        options={events?.map(({ name, id }) => ({ label: name, value: id }))}
        selectedOption={
          selectedEvent
            ? { label: selectedEvent.name, value: selectedEvent.id }
            : null
        }
        onChange={event =>
          event &&
          onEventSelect({ id: event.value as number, name: event.label })
        }
        styles={{
          indicatorSeparator: () => ({}),
        }}
        isLoading={isFetching}
        error={error}
      />
      {isFormStep && <CustomQuestionsSettings entity={entity} />}
      <GroupTitle>color</GroupTitle>

      <ColorPickerControl
        update={updateProp('formFieldTextColor')}
        mobileUpdate={updateProp('mobileFormFieldTextColor')}
        color={entity.formFieldTextColor}
        mobileColor={entity.mobileFormFieldTextColor}
        label={'entity_settings.calendar.form_field_text_color'}
      />
      <ColorPickerControl
        update={updateProp('formFieldPlaceholderColor')}
        mobileUpdate={updateProp('mobileFormFieldPlaceholderColor')}
        color={entity.formFieldPlaceholderColor}
        mobileColor={entity.mobileFormFieldPlaceholderColor}
        label={'entity_settings.calendar.form_field_placeholder_color'}
        isClearable
      />

      <ColorPickerControl
        update={updateProp('titlesColor')}
        mobileUpdate={updateProp('mobileTitlesColor')}
        color={entity.titlesColor}
        mobileColor={entity.mobileTitlesColor}
        label={'entity_settings.calendar.titles_color'}
      />
      <ColorPickerControl
        update={updateProp('color')}
        mobileUpdate={updateProp('mobileColor')}
        color={entity.color}
        mobileColor={entity.mobileColor}
        label={'entity_settings.calendar.text_color'}
        isClearable
      />
      <ColorPickerControl
        update={updateProp('availableSlotBackgroundColor')}
        color={entity.availableSlotBackgroundColor}
        label={'entity_settings.calendar.available_slot_bg_color'}
      />
      <ColorPickerControl
        update={color => {
          updateProp('selectedSlotBackgroundColor')(color)
        }}
        color={entity.selectedSlotBackgroundColor}
        label={'entity_settings.calendar.selected_slot_bg_color'}
      />
      {isFormStep && (
        <React.Fragment>
          <Border
            update={updateProp('fieldBorder')}
            mobileUpdate={updateProp('mobileFieldBorder')}
            border={entity.fieldBorder}
            mobileBorder={entity.mobileFieldBorder}
          />
          <ShadowStyle
            shadow={entity.fieldBoxShadow}
            mobileShadow={entity.mobileFieldBoxShadow}
            update={updateProp('fieldBoxShadow')}
            mobileUpdate={updateProp('mobileFieldBoxShadow')}
          />
        </React.Fragment>
      )}
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={value =>
          updateProp(isMobile ? 'mobileMargin' : 'margin')(value as MarginType)
        }
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </React.Fragment>
  )
}

export default BookingCalendarSettings
